import API from './API';

// -----------------------------------------------------------------------------
//  LISTA BOOKING — caricamento a batch
//
//  Le prenotazioni si chiedono per finestre temporali e i filtri li applica il
//  database: scaricarle tutte supererebbe il tetto del backend
//  (DEFAULT_CRUD_LIMIT=1000), oltre il quale le righe sparirebbero senza errore.
//
//  Un booking contiene PIÙ TRATTE (andata/ritorno) nelle collezioni
//  pickup_bookings / escort_bookings / delivery_bookings: la paginazione è
//  sui BOOKING, mentre a video ogni tratta è una riga.
// -----------------------------------------------------------------------------

// Dati necessari alla riga: tratte con punti e driver, cliente, sede, partner.
// Il jolly NON è incluso: vive su `planner`, che /booking non espone.
const LIST_JOIN = [
  'car',
  'customer',
  'headquarter',
  'headquarter.partner',
  'tasks',
  'pickup_bookings',
  'pickup_bookings.point',
  'pickup_bookings.driver_pickup',
  // Il JOLLY vive su `planner`, non sulla gamba. Senza questi join la lista
  // non sa se una corsa richiede un secondo operatore né chi sia, e la coda
  // "Da assegnare" considera assegnata una corsa a cui manca ancora il jolly.
  // ⚠️ `planner.jolly` NON è il jolly: è il driver assegnato a quel planner.
  // Il ruolo lo dice `planner.type` ('driver' | 'jolly').
  'pickup_bookings.planners',
  'pickup_bookings.planners.jolly',
  'escort_bookings',
  'escort_bookings.point_origin_escort',
  'escort_bookings.point_destination_escort',
  'escort_bookings.driver_escort',
  'escort_bookings.planners',
  'escort_bookings.planners.jolly',
];

function normalize(response, fallbackPage = 1) {
  const body = response?.data;
  if (Array.isArray(body)) {
    return { rows: body, total: body.length, page: fallbackPage, pageCount: 1 };
  }
  return {
    rows: body?.data ?? [],
    total: body?.total ?? 0,
    page: body?.page ?? fallbackPage,
    pageCount: body?.pageCount ?? 1,
  };
}

// ⚠️ GIORNO ITALIANO, non UTC: con `toISOString()` fra mezzanotte e le due
// «oggi» sarebbe ancora ieri, e i segmenti di oggi risulterebbero vuoti.
const iso = (d) =>
  new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Rome", year: "numeric", month: "2-digit", day: "2-digit",
  }).format(new Date(d));
const shift = (days) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return iso(d);
};
/** Ultimo giorno del mese, in `YYYY-MM-DD`. */
const fineMese = (year, month) =>
  `${year}-${String(month).padStart(2, '0')}-${new Date(year, month, 0).getDate()}`;

// Tetto di sicurezza: la board è per operare sulla giornata, non per esportare
// lo storico. Se una finestra lo supera, la UI lo dichiara.
export const WINDOW_CAP = 300;

// I segmenti sono FINESTRE TEMPORALI, non filtri sullo stato di assegnazione.
//
// Perché: le prenotazioni vivono in due tabelle diverse (booking e journey) e
// vanno mostrate insieme, in ordine di partenza. Due sorgenti impaginate
// separatamente non si possono fondere in una sola lista impaginata coerente;
// limitando invece l'intervallo di date i due insiemi restano piccoli, si
// uniscono e si ordinano correttamente.
//
// "Da assegnare" NON è filtrabile dal server: il driver sta sulla singola
// tratta e il jolly addirittura su un'altra tabella (planner) che /booking non
// espone. Si carica la finestra e si selezionano le tratte scoperte a valle.
/**
 * Il giorno dopo quello indicato, in `YYYY-MM-DD`.
 * Serve a chiudere le finestre con un confronto ESCLUSIVO: vedi la nota sul
 * filtro in `getBookingsInWindow`.
 */
function dayAfter(isoDate) {
  const d = new Date(`${isoDate}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
}

export function segmentWindow(segment, periodo = null) {
  switch (segment) {
    case 'assegnare': return { from: iso(new Date()), to: shift(60) };
    case 'conferma': return { from: shift(-30), to: shift(365) };
    case 'attivi': return { from: iso(new Date()), to: iso(new Date()) };
    case 'futuri': return { from: shift(1), to: shift(60) };
    // Conclusi: un MESE per volta, così si scorre tutto lo storico tenendo
    // insieme prenotazioni e spostamenti (due sorgenti impaginate a parte non
    // si potrebbero fondere in un ordine unico). Il mese in corso arriva a
    // oggi compreso: una corsa conclusa stamattina si deve vedere subito.
    case 'passati': {
      const oggi = iso(new Date());
      if (!periodo) return { from: shift(-30), to: oggi };
      const fine = fineMese(periodo.year, periodo.month);
      return {
        from: `${periodo.year}-${String(periodo.month).padStart(2, '0')}-01`,
        to: fine < oggi ? fine : oggi,
      };
    }
    // Passate ma mai chiuse: non sono «concluse» (lo stato non è `end`) e non
    // sono attive (la data è passata), quindi non comparivano da nessuna parte.
    case 'dachiudere': return { from: shift(-365), to: shift(-1) };
    default: return { from: shift(-30), to: shift(60) };
  }
}

export default {
  // Compensi dei driver della prenotazione, una riga per gamba: il server
  // precompila con la tariffa dell'agenzia (ore × tariffa oraria) e dice
  // quale importo è stato deciso a mano. Solo Simba: un 403 qui significa
  // "non sei un ruolo globale", non "non ci sono compensi".
  getDriverCompensation(bookingId) {
    return API.get(`/booking/${bookingId}/driver-compensation`);
  },

  // `amount: null` su una gamba cancella la decisione e la riporta al calcolo.
  setDriverCompensation(bookingId, legs) {
    return API.patch(`/booking/${bookingId}/driver-compensation`, { legs });
  },

  // Booking della finestra. Nessun filtro sull'assegnazione: quello lo fa la
  // pagina dopo aver unito le tratte dei due mondi.
  getBookingsInWindow({
    segment = 'assegnare', partnerId = '', provinceId = '', search = '', periodo = null,
  } = {}) {
    const { from, to } = segmentWindow(segment, periodo);
    // ⚠️ Le condizioni viaggiano in `s` (JSON), non in `filter[]`.
    //
    // La ricerca cliente va su NOME O COGNOME, e con `filter[]` le condizioni
    // sono solo in AND. E quando c'è `s`, @nestjsx/crud IGNORA `filter` e
    // `or`: per questo tutte le condizioni della finestra stanno qui dentro,
    // non solo quella della ricerca.
    const and = [
      { state: { $ne: 'canceled' } },
      { data: { $gte: from } },
      // ⚠️ LIMITE SUPERIORE ESCLUSIVO, sul giorno DOPO.
      //
      // `data` è un datetime e la finestra è fatta di date nude: `data <=
      // '2026-08-28'` significa `<= 2026-08-28 00:00:00`, quindi combacerebbe
      // solo una corsa esattamente a mezzanotte e l'ultimo giorno della finestra
      // resterebbe fuori (i segmenti «Attivi» e «Passati» sarebbero vuoti).
      //
      // È la stessa convenzione usata per i journey.
      { data: { $lt: dayAfter(to) } },
    ];
    if (segment === 'conferma') and.push({ state: { $eq: 'before' } });
    // «Attivi» è la giornata di oggi ANCORA DA FARE e «Da assegnare» è il
    // lavoro da smistare: una corsa conclusa — dal gestionale o dall'app del
    // driver — passa in «Conclusi», che comprende oggi, e non deve restare
    // anche qui. Su «Da assegnare» restava in coda per sempre quando le
    // mancava il jolly.
    if (segment === 'attivi' || segment === 'assegnare') {
      and.push({ state: { $ne: 'end' } });
    }
    if (segment === 'passati') and.push({ state: { $eq: 'end' } });
    if (segment === 'dachiudere') and.push({ state: { $notin: ['end', 'canceled'] } });
    if (partnerId) and.push({ 'headquarter.partner.id': { $eq: partnerId } });
    // provincia della SEDE del booking (colonna FK, niente join in più)
    if (provinceId) and.push({ 'headquarter.provinceId': { $eq: provinceId } });

    // Ogni parola scritta deve comparire nel nome O nel cognome: così
    // «Carla», «Cliente» e «Carla Cliente» trovano tutti la stessa persona.
    const parole = String(search || '').trim().split(/\s+/).filter(Boolean);
    for (const parola of parole) {
      and.push({
        $or: [
          { 'customer.name': { $contL: parola } },
          { 'customer.surname': { $contL: parola } },
        ],
      });
    }

    return API.get('/booking', {
      params: {
        limit: WINDOW_CAP,
        s: JSON.stringify({ $and: and }),
        join: LIST_JOIN,
        sort: 'data,ASC',
      },
    }).then((r) => normalize(r));
  },

  // Quante prenotazioni concluse ci sono IN TUTTO (non solo nella finestra
  // mostrata). Si chiede UNA riga: quello che serve è `total`, che il server
  // calcola comunque. Conta PRENOTAZIONI, non tratte: un andata e ritorno è 1.
  countConcluse({ partnerId = '', provinceId = '', search = '' } = {}) {
    const and = [{ state: { $eq: 'end' } }];
    if (partnerId) and.push({ 'headquarter.partner.id': { $eq: partnerId } });
    if (provinceId) and.push({ 'headquarter.provinceId': { $eq: provinceId } });
    for (const parola of String(search || '').trim().split(/\s+/).filter(Boolean)) {
      and.push({
        $or: [
          { 'customer.name': { $contL: parola } },
          { 'customer.surname': { $contL: parola } },
        ],
      });
    }
    return API.get('/booking', {
      params: { page: 1, limit: 1, s: JSON.stringify({ $and: and }) },
    }).then((r) => normalize(r).total);
  },

  getBookingById(id) {
    return API.get(`/booking/` + id, {
      params: {
        join: [
          'pickup_bookings',
          'pickup_bookings.point',
          'pickup_bookings.driver_pickup',
          'pickup_bookings.planners',

          'delivery_bookings',
          'delivery_bookings.point_origin_delivery',
          'delivery_bookings.point_destination_delivery',
          'delivery_bookings.driver_delivery',
          'delivery_bookings.planners',

          'escort_bookings',
          'escort_bookings.point_origin_escort',
          'escort_bookings.point_destination_escort',
          'escort_bookings.driver_escort',
          'escort_bookings.planners',

          'customer',
          'tasks',
          'car',
          'headquarter',
          'headquarter.area',
          // La PROVINCIA della sede: è con quella che si cercano i driver
          // (l'`area` è la vecchia zona, e le sedi nuove non ce l'hanno).
          'headquarter.province_ref',
          'headquarter.partner',
          // Chi ha preso la prenotazione: lo scrive il backend da chi era
          // collegato alla creazione, quindi dice davvero chi è stato.
          'employee',
        ],
      },
    });
  },

  // ------------------------------------------------ FOTO E ALLEGATI ---
  // `skipErrorToast`: un errore qui non deve coprire la pagina con il
  // messaggio globale; la sezione "Foto e allegati" lo mostra da sé.
  // Foto del driver: righe `image` (4 slot) legate alla TRATTA pickup o
  // delivery, non al booking, e non raggiungibili via join da /booking.
  //
  // ⚠️ NON filtrare sulla relazione (`filter=pickup||$eq||<id>`): il server
  // risponde 500. Si filtra invece sulle CHIAVI dello storage, che contengono
  // l'id della tratta (`partners/<partner>/<pickup|delivery>/<idTratta>/…`,
  // stesso formato di /image/create e dello script di migrazione F2).
  // `fields` tiene fuori le colonne longtext img1..4.
  getRaceImages(kind, raceId) {
    const needle = `/${kind}/${raceId}/`;
    return API.get('/image', {
      params: {
        s: JSON.stringify({
          $or: [1, 2, 3, 4].map((n) => ({ [`key${n}`]: { $cont: needle } })),
        }),
        fields: 'id,key1,key2,key3,key4',
      },
      skipErrorToast: true,
    }).then((r) => r.data?.data ?? r.data ?? []);
  },

  // Pre-signed URL a scadenza, { url1..url4 } (null se lo slot è vuoto).
  getImageUrls(imageId) {
    return API.get(`/image/${imageId}/urls`, { skipErrorToast: true }).then((r) => r.data || {});
  },

  // Allegati liberi (scontrini, foto extra), ognuno con `url` pre-firmato.
  getAttachments(bookingId) {
    return API.get(`/booking/${bookingId}/attachments`, { skipErrorToast: true }).then((r) => r.data || []);
  },

  // Data-URI base64 + nome del file: il server ne ricava tipo ed estensione.
  uploadAttachment(bookingId, { file, fileName }) {
    return API.post(`/booking/${bookingId}/attachments`, {
      file,
      file_name: String(fileName || '').slice(0, 255) || undefined,
    });
  },

  deleteAttachment(bookingId, attachmentId) {
    return API.delete(`/booking/${bookingId}/attachments/${attachmentId}`);
  },




  updateDateBooking(id, data) {
    return API.patch('/booking/' + id, data);
  },

  /***************************PICKUP**********************/
  getRangePickup(req) {
    return API.post('/pickup/range', req);
  },
  getRangeRestaurant(req){
    return API.post('/pickup/range-restaurant', req);
  },
  updateDatePickup(id, date) {
    return API.post('/pickup/update-prenotazione/' + id, date);
  },

  /***************************ESCORT **********************/

  getRangeEscort(req) {
    return API.post('/escort/range', req);
  },

  updateDateEscort(id, date) {
    return API.post('/escort/update-prenotazione/' + id, date);
  },

  /***************************DELIVERY**********************/

  updateDateDelivery(id, date) {
    return API.post('/delivery/update-prenotazione/' + id, date);
  },

  messageModifyHourBooking(data) {
    return API.post('/booking/messageModifyHourBooking/', data);
  },


  // Dati della prenotazione (note, mezzo, passeggeri): PATCH del CRUD, che
  // scrive solo i campi mandati. Tratte, planner e assegnazioni non cambiano.
  updateBooking(id, data) {
    return API.patch(`/booking/${id}`, data);
  },

  // Prezzo del Transfer: imponibile (`customer_price`) e totale IVA inclusa
  // (`tot_fattura`), così com'è stato scritto.
  setPricing(id, data) {
    return API.patch(`/booking/${id}/pricing`, data);
  },

  // Cambio indirizzo di una tratta (solo Simba): km, fascia, prezzo e turni
  // ricalcolati tenendo i driver. Con `dryRun: true` solo il confronto.
  changeLegAddress(id, data) {
    return API.patch(`/booking/${id}/leg-address`, data);
  },

  // Cambio cliente e/o auto (solo Simba): il valet segue l'auto.
  changeCustomerCar(id, data) {
    return API.patch(`/booking/${id}/customer-car`, data);
  },

  // Cambio partner e sede, solo Simba: km, prezzo e turni di tutte le tratte
  // si ricalcolano con la nuova sede e il listino del suo partner.
  // `dryRun: true` restituisce solo il confronto prima/dopo.
  changePartnerSede(id, data) {
    return API.patch(`/booking/${id}/partner-sede`, data);
  },

  patchBooking(id) {
    return API.patch('/state', id);
  },

  deleteBooking(id) {
    return API.get('/booking/delete/' + id);
  },

  // Importi della prenotazione. Il CRUD accetta la patch parziale: si manda
  // solo quello che si sta cambiando.
  updateImporti(id, { imponibile, tot_fattura }) {
    const body = {};
    if (imponibile !== undefined) body.imponibile = imponibile;
    if (tot_fattura !== undefined) body.tot_fattura = tot_fattura;
    return API.patch('/booking/' + id, body);
  },

  // Voce che il cliente paga DALL'APP: è un `task` della prenotazione, ed è la
  // sorgente del COD5. `pay` = pagabile in app, `accepted` = saldato, quindi
  // nasce da pagare.
  //
  // ⚠️ Il partner va indicato: il server lo ricava dal token di chi scrive, e
  // un utente Simba non ne ha uno — senza, la voce resterebbe senza tenant e
  // non comparirebbe nella contabilità di quel partner.
  addPagamentoInApp({ bookingId, partnerId, descrizione, importo }) {
    return API.post('/task', {
      description: descrizione,
      cost: importo,
      pay: true,
      accepted: false,
      booking: { id: bookingId },
      // Colonna, non relazione: su Task la relazione si chiama
      // `partner_tenant`, e un `partner` nel body verrebbe ignorato.
      ...(partnerId ? { partnerId } : {}),
    });
  },

  // ------------------------------------------------------- CREAZIONE ------
  //  ⚠️ Il booking si crea con le TRATTE ANNIDATE dentro il corpo
  //  (pickup_bookings / escort_bookings), planner compresi: non esistono
  //  chiamate separate per aggiungere una tratta dopo.
  //
  //  ⚠️ Il server SOVRASCRIVE `type_booking` in base al partner
  //  (applyBookingCapability): is_restaurant -> 'restaurant', altrimenti
  //  'booking'. Il notturno quindi non si sceglie: dipende dal partner. Un
  //  partner senza `is_restaurant` né `booking_type` riceve 403.
  addBooking(booking) {
    return API.post('/booking/', booking);
  },

  // Notifica al cliente della prenotazione appena creata. È best-effort: il
  // booking a questo punto esiste già, un errore qui non va confuso con un
  // fallimento della creazione.
  addBookingNotification(data) {
    return API.post('/booking/messageNewBooking/', data);
  },
};
